// FIXED: src/components/orders/ProductRequestsTable.jsx (for vendor project)
import React, { useState, useEffect } from 'react';
import Button from '../common/Button';

const ProductRequestsTable = ({ requests, loading, onResponse }) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseQuantity, setResponseQuantity] = useState(0);
  const [responseAction, setResponseAction] = useState('');
  const [timeRemaining, setTimeRemaining] = useState({});

  useEffect(() => {
    const updateTimeRemaining = () => {
      const newTimeRemaining = {};
      requests.forEach(request => {
        newTimeRemaining[request.id] = getTimeRemaining(request.expiresAt);
      });
      setTimeRemaining(newTimeRemaining);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000);
    return () => clearInterval(interval);
  }, [requests]);

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return "No expiry";
    
    try {
      const now = new Date();
      const expiry = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
      const diff = expiry.getTime() - now.getTime();
      
      if (diff <= 0) return "Expired";
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else if (minutes > 0) {
        return `${minutes}m`;
      } else {
        return "< 1m";
      }
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return "Invalid date";
    }
  };

  const getTimeRemainingColor = (expiresAt) => {
    if (!expiresAt) return "text-gray-500";
    
    try {
      const now = new Date();
      const expiry = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
      const diff = expiry.getTime() - now.getTime();
      
      if (diff <= 0) return "text-red-600";
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      
      if (hours <= 1) return "text-red-600";
      if (hours <= 6) return "text-yellow-600";
      return "text-green-600";
    } catch (error) {
      return "text-gray-500";
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getUrgencyBadge = (level) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      normal: 'bg-blue-100 text-blue-800',
      high: 'bg-yellow-100 text-yellow-800',
      urgent: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[level] || colors.normal}`}>
        {level?.charAt(0).toUpperCase() + level?.slice(1)}
      </span>
    );
  };

  const handleResponse = (request, action) => {
    console.log('Handling response for request:', request.id, action);
    setSelectedRequest(request);
    setResponseAction(action);
    setResponseQuantity(action === 'accept' ? (request.requestedQuantity || 1) : 0);
    setShowResponseModal(true);
  };

  const submitResponse = async () => {
    if (!selectedRequest || !responseAction) return;
    
    console.log('Submitting response:', {
      requestId: selectedRequest.id,
      action: responseAction,
      quantity: responseQuantity
    });
    
    try {
      await onResponse(selectedRequest.id, responseAction, responseQuantity);
      setShowResponseModal(false);
      setSelectedRequest(null);
      setResponseAction('');
      setResponseQuantity(0);
    } catch (error) {
      console.error('Error submitting response:', error);
    }
  };

  const closeModal = () => {
    setShowResponseModal(false);
    setSelectedRequest(null);
    setResponseAction('');
    setResponseQuantity(0);
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    try {
      const now = new Date();
      const expiry = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
      return now > expiry;
    } catch (error) {
      console.error('Error checking expiry:', error);
      return false;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        <p className="ml-3 text-gray-600">Loading product requests...</p>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No product requests</h3>
        <p className="mt-1 text-sm text-gray-500">
          Product requests from mechanics will appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Product Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Mechanic
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Quantity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Time Left
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requests.map((request) => {
              // Safely access product data
              const product = request.product || {};
              const productName = product.name || request.productName || 'Unknown Product';
              const productPrice = product.price || 0;
              const productImage = product.imageUrl || request.productImage;
              const partNumber = product.partNumber || request.partNumber || 'N/A';
              const category = product.category || request.category || 'Uncategorized';
              const urgencyLevel = product.urgencyLevel || request.urgencyLevel || 'normal';
              
              return (
                <tr key={request.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        {productImage ? (
                          <img
                            className="h-12 w-12 rounded-lg object-cover"
                            src={productImage}
                            alt={productName}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center"
                          style={{ display: productImage ? 'none' : 'flex' }}
                        >
                          <span className="text-gray-500 font-medium">
                            {productName.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {productName}
                        </div>
                        <div className="text-sm text-gray-500">
                          Part: {partNumber}
                        </div>
                        <div className="text-sm text-gray-500">
                          ₹{productPrice}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {category}
                        </div>
                        {urgencyLevel && (
                          <div className="mt-1">
                            {getUrgencyBadge(urgencyLevel)}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 font-medium">
                      {request.mechanicName || 'Unknown Mechanic'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {request.mechanicEmail || ''}
                    </div>
                    {request.mechanicPhone && (
                      <div className="text-sm text-gray-500">
                        {request.mechanicPhone}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 font-medium">
                      {request.requestedQuantity || 1}
                    </div>
                    {product.minQuantity && (
                      <div className="text-xs text-gray-500">
                        Min: {product.minQuantity}
                      </div>
                    )}
                    {request.approvedQuantity && (
                      <div className="text-sm text-green-600 font-medium">
                        Approved: {request.approvedQuantity}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm font-medium ${getTimeRemainingColor(request.expiresAt)}`}>
                      {timeRemaining[request.id] || getTimeRemaining(request.expiresAt)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate(request.expiresAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(request.status)}
                  </td>
                  <td className="px-6 py-4">
                    {request.status === 'pending' && !isExpired(request.expiresAt) ? (
                      <div className="flex space-x-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleResponse(request, 'accept')}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleResponse(request, 'reject')}
                        >
                          Reject
                        </Button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">
                        {request.status === 'accepted' ? 'Accepted' : 
                         request.status === 'rejected' ? 'Rejected' : 
                         isExpired(request.expiresAt) ? 'Expired' : 'No action needed'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Response Modal */}
      {showResponseModal && selectedRequest && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                    responseAction === 'accept' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {responseAction === 'accept' ? (
                      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {responseAction === 'accept' ? 'Accept' : 'Reject'} Product Request
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {responseAction === 'accept' 
                          ? `Accept the request for ${selectedRequest.product?.name || selectedRequest.productName || 'this product'}?`
                          : `Reject the request for ${selectedRequest.product?.name || selectedRequest.productName || 'this product'}?`
                        }
                      </p>
                      
                      <div className="mt-4 p-4 bg-gray-50 rounded-md">
                        <div className="text-sm space-y-1">
                          <p><strong>Product:</strong> {selectedRequest.product?.name || selectedRequest.productName || 'N/A'}</p>
                          <p><strong>Mechanic:</strong> {selectedRequest.mechanicName || 'N/A'}</p>
                          <p><strong>Requested:</strong> {selectedRequest.requestedQuantity || 1}</p>
                          <p><strong>Price:</strong> ₹{selectedRequest.product?.price || 0}</p>
                          {(selectedRequest.product?.category || selectedRequest.category) && (
                            <p><strong>Category:</strong> {selectedRequest.product?.category || selectedRequest.category}</p>
                          )}
                          {(selectedRequest.product?.urgencyLevel || selectedRequest.urgencyLevel) && (
                            <p><strong>Urgency:</strong> {selectedRequest.product?.urgencyLevel || selectedRequest.urgencyLevel}</p>
                          )}
                        </div>
                      </div>
                      
                      {responseAction === 'accept' && (
                        <div className="mt-4">
                          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                            Quantity you can provide
                          </label>
                          <input
                            type="number"
                            id="quantity"
                            min="1"
                            max={selectedRequest.requestedQuantity}
                            value={responseQuantity}
                            onChange={(e) => setResponseQuantity(parseInt(e.target.value) || 0)}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            Maximum requested: {selectedRequest.requestedQuantity}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    responseAction === 'accept' 
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                  onClick={submitResponse}
                  disabled={responseAction === 'accept' && responseQuantity <= 0}
                >
                  {responseAction === 'accept' ? 'Accept Request' : 'Reject Request'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductRequestsTable;